import React, { useState } from 'react';
import { Container, Typography, Paper, Grid } from '@mui/material';
import MortgageForm, { MortgageData } from './MortgageForm';
import AmortizationSchedule, { AmortizationEntry } from './AmortizationSchedule';

const App: React.FC = () => {
  const [originalSchedule, setOriginalSchedule] = useState<AmortizationEntry[]>([]);
  const [updatedSchedule, setUpdatedSchedule] = useState<AmortizationEntry[]>([]);
  const [mortgageData, setMortgageData] = useState<MortgageData | null>(null);

  const calculateSchedule = (data: MortgageData) => {
    setMortgageData(data);

    // Generar el plan original sin pagos adicionales
    const initialSchedule = generateSchedule(data, []);
    setOriginalSchedule(initialSchedule);

    // Generar el plan actualizado (inicialmente igual al original)
    setUpdatedSchedule(initialSchedule);
  };

  const handleExtraPaymentChange = (month: number, amount: number) => {
    if (!mortgageData) return;

    // Actualizar el pago extra en el mes correspondiente
    const newExtraPayments = updatedSchedule.map((entry) => entry.extraPayment);
    newExtraPayments[month - 1] = amount;

    // Recalcular el plan de amortización actualizado con los pagos extra
    const newUpdatedSchedule = generateSchedule(mortgageData, newExtraPayments);
    setUpdatedSchedule(newUpdatedSchedule);
  };

  const generateSchedule = (data: MortgageData, extraPayments: number[]) => {
    const principal = data.housePrice - data.downPayment;
    const numberOfPayments = data.loanTerm * 12;
    const monthlyInterestRate = data.annualInterestRate / 100 / 12;

    const monthlyPayment =
      (principal * monthlyInterestRate) /
      (1 - Math.pow(1 + monthlyInterestRate, -numberOfPayments));

    let balance = principal;
    const newSchedule: AmortizationEntry[] = [];

    for (let month = 1; month <= numberOfPayments && balance > 0; month++) {
      const interestPayment = balance * monthlyInterestRate;
      let principalPayment = monthlyPayment - interestPayment;

      const extraPayment = extraPayments[month - 1] || 0;
      principalPayment += extraPayment;

      balance -= principalPayment;
      balance = Math.max(balance, 0);

      newSchedule.push({
        month,
        interestPayment,
        principalPayment,
        extraPayment,
        totalPayment: monthlyPayment + extraPayment,
        remainingBalance: balance,
      });

      // Si el saldo es cero, terminamos el bucle
      if (balance <= 0) {
        break;
      }
    }

    return newSchedule;
  };

  const calculateTotals = (schedule: AmortizationEntry[]) => {
    const totalInterest = schedule.reduce((sum, entry) => sum + entry.interestPayment, 0);
    const totalPayments = schedule.length;
    return { totalInterest, totalPayments };
  };

  const originalTotals = calculateTotals(originalSchedule);
  const updatedTotals = calculateTotals(updatedSchedule);

  return (
    <Container maxWidth="md">
      <Typography variant="h4" align="center" gutterBottom style={{ marginTop: '20px' }}>
        Calculadora de Préstamo Hipotecario
      </Typography>
      <MortgageForm onCalculate={calculateSchedule} />
      {originalSchedule.length > 0 && updatedSchedule.length > 0 && (
        <>
          <AmortizationSchedule
            originalSchedule={originalSchedule}
            updatedSchedule={updatedSchedule}
            onExtraPaymentChange={handleExtraPaymentChange}
          />
          <Paper elevation={3} style={{ padding: '20px', marginBottom: '20px' }}>
            <Typography variant="h6">Resumen</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">Plan Original</Typography>
                <Typography>Total de Intereses Pagados: {originalTotals.totalInterest.toFixed(2)} €</Typography>
                <Typography>Duración del Préstamo: {originalTotals.totalPayments} meses</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1">Plan Actualizado</Typography>
                <Typography>Total de Intereses Pagados: {updatedTotals.totalInterest.toFixed(2)} €</Typography>
                <Typography>Duración del Préstamo: {updatedTotals.totalPayments} meses</Typography>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Container>
  );
};

export default App;
